import { Injectable, signal, computed } from "@angular/core";
import { JobIssueWithDetails } from "../../../../../../domains/jobIssues/models/JobIssueWithDetails";
import { UNASSIGNED_AREA } from "../../../../../../domains/areas/models/AreaDTO";
import { SELF_ASSIGNED_VENDOR } from "../../../../../../domains/vendors/models/VendorDTO";
import { VendorDeliveryState } from "../../../../../../domains/vendors/models/JobIssueVendorDTO";
import { getBqStepDisplay } from "../../../../../../domains/jobs/models/build-quality-step-display";
import { determinVendorAssignmentStatesForIssue } from "../../../../../../domains/jobIssues/services/job-issue-vendor.service";
import {
  collectAvailableValues,
  countSelected,
  FilterChip,
  FilterDefinition as SharedFilterDefinition,
  FilterOption,
  FilterSelection,
  filterItems,
  removeSelectedValue,
  toFilterChips
} from "../../../../../../shared/components/filter-panel/filter-panel.model";

export const UNASSIGNED_VENDOR_FILTER_VALUE = "__unassigned-vendor__";
export const UNASSIGNED_VENDOR_FILTER_LABEL = "Unassigned";

export const VENDOR_DELIVERY_FILTER_LABELS: Record<VendorDeliveryState, string> = {
  [VendorDeliveryState.NoVendorAssigned]: "No Vendor Assigned",
  [VendorDeliveryState.ContactRequired]: "Contact Required",
  [VendorDeliveryState.NotSent]: "Unsent",
  [VendorDeliveryState.Sent]: "Sent",
  [VendorDeliveryState.Viewed]: "Viewed"
};

export const DUE_FILTER_ID = "due";

export enum DueFilterValue {
  Overdue = "Overdue",
  DueToday = "Due Today",
  Upcoming = "Upcoming",
  NoDueDate = "No Due Date"
}

function toDateOnly(value: Date | string): number {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function getDueFilterValue(issue: Pick<JobIssueWithDetails, "dueDate">): DueFilterValue {
  if (!issue.dueDate) {
    return DueFilterValue.NoDueDate;
  }

  const dueDate = toDateOnly(issue.dueDate);
  const today = toDateOnly(new Date());

  if (dueDate < today) {
    return DueFilterValue.Overdue;
  }

  return dueDate === today ? DueFilterValue.DueToday : DueFilterValue.Upcoming;
}

export type FilterDefinition = SharedFilterDefinition<JobIssueWithDetails>;

export type ActiveFilterChip = FilterChip;

/** Self-assigned first, then unassigned, then the trades alphabetically. */
function compareVendorOptions(left: FilterOption, right: FilterOption): number {
  for (const pinned of [SELF_ASSIGNED_VENDOR.name, UNASSIGNED_VENDOR_FILTER_LABEL]) {
    if (left.label === pinned) {
      return -1;
    }

    if (right.label === pinned) {
      return 1;
    }
  }

  return left.label.localeCompare(right.label);
}

/** Unassigned sinks to the bottom; every named room sorts alphabetically above it. */
function compareRoomOptions(left: FilterOption, right: FilterOption): number {
  if (left.label === UNASSIGNED_AREA.name) {
    return 1;
  }

  if (right.label === UNASSIGNED_AREA.name) {
    return -1;
  }

  return left.label.localeCompare(right.label);
}

@Injectable({
  providedIn: "root"
})
export class FilterService {
  private readonly _selected = signal<FilterSelection>({});
  readonly selected = this._selected.asReadonly();

  readonly definitions: FilterDefinition[] = [
    {
      id: "bq-step",
      label: "BQ Step",
      getValue: issue => {
        const mainBQStep = (issue.buildQualityStep !== null && issue.buildQualityStep !== undefined
          ? getBqStepDisplay(issue.buildQualityStep)
          : null);
        const ancestorBqSteps = issue.ancestorJobIssues.map(x => (x.buildQualityStep !== null && x.buildQualityStep !== undefined ? getBqStepDisplay(x.buildQualityStep) : null));
        return [mainBQStep, ...ancestorBqSteps].filter(v => v !== null);
      },
      availableValues: [],
      getLabel: v => v
    },
    {
      id: "room",
      label: "Room",
      getValue: issue => (issue.isAreaUnassigned ? UNASSIGNED_AREA.name : (issue.areaName ?? null)),
      availableValues: [],
      getLabel: v => v,
      compareOptions: compareRoomOptions
    },
    {
      id: "status",
      label: "Status",
      getValue: issue => {
        const deliveryStatesForIssue = determinVendorAssignmentStatesForIssue(issue, issue.assignedVendors, issue.assignedVendors.flatMap(v => v.vendorDetails.userVendorContacts));
        return Object.values(deliveryStatesForIssue)
          .filter(state => state !== VendorDeliveryState.NoVendorAssigned)
          .map(state => state.toString());
      },
      availableValues: [],
      getLabel: v => VENDOR_DELIVERY_FILTER_LABELS[v as VendorDeliveryState] ?? v
    },
    {
      id: DUE_FILTER_ID,
      label: "Due",
      getValue: issue => getDueFilterValue(issue),
      availableValues: [],
      getLabel: v => v
    },
    {
      id: "vendor",
      label: "Vendor",
      getValue: issue => {
        if (!issue.isSelfAssigned && issue.assignedVendors.length === 0) {
          return [UNASSIGNED_VENDOR_FILTER_VALUE];
        }
        const vendorNames = issue.assignedVendors?.map(v => v.vendorDetails.name) ?? [];
        if (issue.isSelfAssigned) {
          vendorNames.push(SELF_ASSIGNED_VENDOR.name);
        }
        return vendorNames;
      },
      availableValues: [],
      getLabel: v => (v === UNASSIGNED_VENDOR_FILTER_VALUE ? UNASSIGNED_VENDOR_FILTER_LABEL : v),
      compareOptions: compareVendorOptions
    }
  ];

  readonly selectedCount = computed(() => countSelected(this.definitions, this._selected()));

  readonly activeChips = computed<ActiveFilterChip[]>(() =>
    toFilterChips(this.definitions, this._selected()));

  initializeAvailableValues(issues: JobIssueWithDetails[]): void {
    collectAvailableValues(issues, this.definitions);
  }

  filterIssues(issues: JobIssueWithDetails[], selected: FilterSelection): JobIssueWithDetails[] {
    return filterItems(issues, this.definitions, selected);
  }

  applyFilters(issues: JobIssueWithDetails[]): JobIssueWithDetails[] {
    return this.filterIssues(issues, this._selected());
  }

  applyDraft(draft: FilterSelection): void {
    this._selected.set({ ...draft });
  }

  /** Replaces every active filter with a single category selection, e.g. when deep-linking to overdue items. */
  replaceWith(filterId: string, values: string[]): void {
    this._selected.set({ [filterId]: values });
  }

  removeChip(filterId: string, value: string): void {
    this._selected.update(current => removeSelectedValue(current, filterId, value));
  }

  clear(): void {
    this._selected.set({});
  }
}

